import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { athletesAPI, getErrorMessage } from '../lib/api';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Copy, Check, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PaywallModal from '../components/PaywallModal';

const ITEMS_PER_PAGE = 20;

export default function AthletesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const emptyFormData = useMemo(() => ({ name: '', email: '' }), []);
  const [formData, setFormData] = useState(emptyFormData);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [showPaywall, setShowPaywall] = useState(false);

  // Invite link popup states
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [onboardingAdvanceTo, setOnboardingAdvanceTo] = useState(null);

  // Codice atleta popup states
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [createdAthleteCode, setCreatedAthleteCode] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);

  // Conferma submit senza email
  const [noEmailConfirmPending, setNoEmailConfirmPending] = useState(false);

  useEffect(() => {
    fetchAthletes();
  }, []);

  useEffect(() => {
    if (onboardingAdvanceTo === null) {
      return;
    }
    if (showInviteLinkModal) {
      return;
    }
    window.dispatchEvent(new CustomEvent('onboarding:advance', { detail: { step: onboardingAdvanceTo } }));
    setOnboardingAdvanceTo(null);
  }, [onboardingAdvanceTo, showInviteLinkModal]);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setEditingAthlete(null);
      setFormData(emptyFormData);
      setDialogOpen(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('create');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, emptyFormData]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    if (athletes.length === 0) return;
    const target = athletes.find((athlete) => athlete.id === editId);
    if (target) {
      setEditingAthlete(target);
      setFormData({
        name: target.name || '',
        email: target.email || '',
      });
      setDialogOpen(true);
    } else {
      toast.error('Atleta non trovato');
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, athletes]);

  const fetchAthletes = async () => {
    try {
      const response = await athletesAPI.getAll();
      // Ordina atleti in ordine alfabetico per nome
      const sortedAthletes = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
      );
      setAthletes(sortedAthletes);
    } catch (error) {
      toast.error('Errore nel caricamento degli atleti');
    } finally {
      setLoading(false);
    }
  };

  // Paginazione
  const totalPages = Math.ceil(athletes.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedAthletes = athletes.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Se non c'è email in creazione, chiedi conferma prima di procedere
    if (!editingAthlete && !formData.email && !noEmailConfirmPending) {
      setNoEmailConfirmPending(true);
      return;
    }

    try {
      if (editingAthlete) {
        await athletesAPI.update(editingAthlete.id, formData);
        toast.success('Atleta aggiornato!');
      } else {
        const response = await athletesAPI.create(formData);
        toast.success('Atleta creato!');

        const athleteCode = response.data.athlete?.athlete_code;

        // Se c'è un link di invito, mostra il popup con anche il codice
        if (response.data.invitation_link) {
          setInviteLink(response.data.invitation_link);
          setLinkCopied(false);
          if (athleteCode) setCreatedAthleteCode(athleteCode);
          setShowInviteLinkModal(true);
        } else if (athleteCode) {
          // Nessun link di invito (email assente): mostra solo il codice
          setCreatedAthleteCode(athleteCode);
          setCodeCopied(false);
          setShowCodeModal(true);
        }

        if (sessionStorage.getItem('onboarding_active') === 'true') {
          if (response.data.invitation_link) {
            setOnboardingAdvanceTo(3);
          } else {
            window.dispatchEvent(new CustomEvent('onboarding:advance', { detail: { step: 3 } }));
          }
        }
      }
      setDialogOpen(false);
      setFormData(emptyFormData);
      setEditingAthlete(null);
      setNoEmailConfirmPending(false);
      fetchAthletes();
    } catch (error) {
      setNoEmailConfirmPending(false);
      if (error?.response?.status === 402 && error?.response?.data?.detail === 'ATHLETE_LIMIT_REACHED') {
        setDialogOpen(false);
        setShowPaywall(true);
      } else {
        toast.error('Errore durante il salvataggio');
      }
    }
  };

  const handleCopyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      toast.success('Link copiato negli appunti!');
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error) {
      toast.error('Errore nella copia del link');
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(createdAthleteCode);
      setCodeCopied(true);
      toast.success('Codice copiato negli appunti!');
      setTimeout(() => setCodeCopied(false), 3000);
    } catch (error) {
      toast.error('Errore nella copia del codice');
    }
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setNoEmailConfirmPending(false);
      setFormData(emptyFormData);
      setEditingAthlete(null);
    }
  };

  const handleEdit = (athlete) => {
    setEditingAthlete(athlete);
    setFormData({ name: athlete.name, email: athlete.email || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo atleta?')) return;

    try {
      await athletesAPI.delete(id);
      toast.success('Atleta eliminato');
      fetchAthletes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore durante l\'eliminazione'));
    }
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div data-testid="athletes-page" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">Atleti</h1>
            <p className="text-zinc-400 text-sm sm:text-base">Gestisci i tuoi atleti ({athletes.length} totali)</p>
          </div>

          <Button
            onClick={() => {
              setEditingAthlete(null);
              setFormData(emptyFormData);
              setDialogOpen(true);
            }}
            className="hidden md:flex bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl h-10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Atleta
          </Button>

          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogContent
              onInteractOutside={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
              className="bg-zinc-900 border border-white/10 rounded-xl">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingAthlete ? 'Modifica Atleta' : 'Nuovo Atleta'}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingAthlete ? 'Modifica i dettagli dell\'atleta' : 'Aggiungi un nuovo atleta al tuo team'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">Nome</Label>
                  <Input
                    id="name"
                    data-testid="athlete-name-input"
                    value={formData.name}
                    onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setNoEmailConfirmPending(false); }}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-[#ccff00] rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">
                    Email <span className="text-zinc-500 font-normal">(la stessa usata dall'atleta per accedere all'app)</span>
                  </Label>
                  <Input
                    id="email"
                    data-testid="athlete-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setNoEmailConfirmPending(false); }}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-[#ccff00] rounded-xl"
                  />
                  {/* Warning visibile non appena l'email è vuota in modalità creazione */}
                  {!editingAthlete && !formData.email && !noEmailConfirmPending && (
                    <p className="text-amber-400/80 text-xs flex items-start gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Il mancato inserimento dell'email potrebbe impedire l'abbinamento automatico dell'atleta al suo profilo nell'app
                    </p>
                  )}
                  {/* Conferma prominente quando l'utente prova a salvare senza email */}
                  {!editingAthlete && noEmailConfirmPending && (
                    <div className="p-3 bg-amber-950/40 border border-amber-500/40 rounded-xl space-y-2">
                      <p className="text-amber-300 text-sm font-medium flex items-start gap-1.5">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        Senza email l'atleta non potrà essere abbinato automaticamente al suo profilo. Al termine ti verrà mostrato un codice da comunicare manualmente all'atleta.
                      </p>
                      <p className="text-amber-400/70 text-xs">Vuoi procedere comunque senza email?</p>
                    </div>
                  )}
                </div>

                {noEmailConfirmPending ? (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setNoEmailConfirmPending(false)}
                      className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                    >
                      Annulla
                    </Button>
                    <Button
                      data-testid="save-athlete-btn"
                      type="submit"
                      className="flex-1 bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-xl"
                    >
                      Procedi senza email
                    </Button>
                  </div>
                ) : (
                  <Button
                    data-testid="save-athlete-btn"
                    type="submit"
                    className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                  >
                    {editingAthlete ? 'Aggiorna' : 'Crea'}
                  </Button>
                )}
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {athletes.length === 0 ? (
          <div className="glass-card p-12 rounded-xl border border-white/10 text-center">
            <p className="text-zinc-400 text-lg">Nessun atleta trovato. Aggiungi il tuo primo atleta!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 [@media(min-width:768px)_and_(orientation:portrait)]:grid-cols-2 [@media(min-width:768px)_and_(orientation:landscape)]:grid-cols-3 gap-2 sm:gap-3">
              {paginatedAthletes.map((athlete) => (
                <div
                  key={athlete.id}
                  data-testid="athlete-card"
                  className="glass-card p-2.5 rounded-xl border border-white/10 hover:border-[#ccff00]/30 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/athletes/${athlete.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1">
                      <ProfileAvatar 
                        src={athlete.profile_photo} 
                        name={athlete.name}
                        size="lg"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-0 group-hover:text-[#ccff00]">{athlete.name}</h3>
                        {athlete.athlete_code && (
                          <p className="text-zinc-500 text-xs font-mono">Codice: {athlete.athlete_code}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        data-testid="edit-athlete-btn"
                        onClick={() => handleEdit(athlete)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-[#ccff00]"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid="delete-athlete-btn"
                        onClick={() => handleDelete(athlete.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-4 mt-10 mb-4">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Indietro
                </Button>
                <span className="text-zinc-400">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl"
                >
                  Avanti
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}

        <PaywallModal
          open={showPaywall}
          onOpenChange={setShowPaywall}
          onSuccess={fetchAthletes}
        />

        {/* Invite Link Modal */}
        <Dialog open={showInviteLinkModal} onOpenChange={setShowInviteLinkModal}>
          <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="bg-zinc-900 border border-white/10 rounded-xl w-[calc(100vw-24px)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-bold">Link di Invito</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Condividi questo link con l'atleta per permettergli di registrarsi
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-zinc-950 rounded-xl border border-zinc-800">
                <p className="text-zinc-300 text-sm break-all">{inviteLink}</p>
              </div>

              <Button
                onClick={handleCopyInviteLink}
                className={`w-full font-bold rounded-xl ${
                  linkCopied
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-[#ccff00] text-black hover:bg-[#b3e600]'
                }`}
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Link Copiato!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copia Link di Invito
                  </>
                )}
              </Button>

              {/* Codice atleta — utile se l'atleta si registra manualmente */}
              {createdAthleteCode && (
                <div className="p-3 bg-zinc-950 rounded-xl border border-[#ccff00]/20 space-y-1">
                  <p className="text-zinc-400 text-xs uppercase tracking-wide">Codice atleta</p>
                  <p className="text-[#ccff00] font-mono text-xl font-bold tracking-widest">{createdAthleteCode}</p>
                  <p className="text-zinc-500 text-xs">
                    Comunica questo codice all'atleta: lo dovrà inserire nella sezione "Collega Profilo" dell'app se si registra senza usare il link sopra.
                  </p>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setShowInviteLinkModal(false)}
                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
              >
                Chiudi
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Codice Atleta Modal (quando nessuna email inserita) */}
        <Dialog open={showCodeModal} onOpenChange={setShowCodeModal}>
          <DialogContent
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            className="bg-zinc-900 border border-white/10 rounded-xl w-[calc(100vw-24px)] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-bold">Codice Atleta</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Comunica questo codice all'atleta: lo dovrà inserire nell'app per collegarsi al suo profilo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-zinc-950 rounded-xl border border-[#ccff00]/30 text-center">
                <p className="text-zinc-400 text-xs uppercase tracking-widest mb-1">Codice</p>
                <p className="text-[#ccff00] font-mono text-3xl font-black tracking-[0.3em]">{createdAthleteCode}</p>
              </div>

              <div className="p-3 bg-amber-950/30 border border-amber-500/20 rounded-xl">
                <p className="text-amber-300/90 text-sm">
                  L'atleta dovrà inserire questo codice nella sezione <strong>"Collega Profilo"</strong> dell'app dopo aver completato la registrazione.
                </p>
              </div>

              <Button
                onClick={handleCopyCode}
                className={`w-full font-bold rounded-xl ${
                  codeCopied
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-[#ccff00] text-black hover:bg-[#b3e600]'
                }`}
              >
                {codeCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Codice Copiato!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copia Codice
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowCodeModal(false)}
                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
              >
                Chiudi
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </CoachLayout>
  );
}
